import React from 'react';

export type DepthValueItem = {
  label: string;
  value: number | string | null | undefined;
  section?: string;
  unit?: string;
  precision?: number;
  color?: string;
};

type Props = {
  depth: number | null;
  items: DepthValueItem[];
  layout?: 'horizontal' | 'vertical';
  emptyText?: string;
  height?: number;
  title?: string;
};

function formatValue(item: DepthValueItem): string {
  if (typeof item.value === 'number') {
    if (!Number.isFinite(item.value)) return '--';
    return item.value.toFixed(item.precision ?? 2);
  }
  if (typeof item.value === 'string' && item.value.trim()) return item.value;
  return '--';
}

export default function DepthValuePanel({
  depth,
  items,
  layout = 'vertical',
  emptyText = '暂无可显示曲线数据',
  height,
  title = '当前深度属性',
}: Props) {
  const horizontal = layout === 'horizontal';
  const horizontalHeight = Math.max(96, height ?? 96);
  return (
    <section className={`sweetspot-depth-inspector${horizontal ? ' is-horizontal' : ''}`} style={{
      flex: horizontal ? `0 0 ${horizontalHeight}px` : '0 0 220px',
      minWidth: horizontal ? 0 : 220,
      minHeight: 0,
      display: 'flex',
      flexDirection: horizontal ? 'row' : 'column',
      background: '#FFFFFF',
      border: '1px solid #D6DAE0',
      overflow: 'hidden',
    }}>
      <div style={{
        flex: horizontal ? '0 0 168px' : '0 0 auto',
        padding: horizontal ? '12px 16px' : '9px 12px 8px',
        display: horizontal ? 'flex' : undefined,
        flexDirection: horizontal ? 'column' : undefined,
        justifyContent: horizontal ? 'center' : undefined,
        background: '#F4F6F9',
        borderRight: horizontal ? '1px solid #D6DAE0' : 'none',
        borderBottom: horizontal ? 'none' : '1px solid #D6DAE0',
      }}>
        <div style={{ fontSize: 12, color: '#4F6680' }}>{title}</div>
        <div style={{ marginTop: 3, color: '#C2533A', fontSize: 20, fontWeight: 700, lineHeight: '26px' }}>
          {depth == null || !Number.isFinite(depth) ? '--' : `${depth.toFixed(1)} m`}
        </div>
      </div>
      {depth == null ? (
        <div style={{ padding: 16, color: '#8598AD', fontSize: 12 }}>{emptyText}</div>
      ) : (
        <div style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: horizontal ? 'grid' : 'flex',
          gridTemplateColumns: horizontal ? 'repeat(6, minmax(112px, 1fr))' : undefined,
          gridTemplateRows: horizontal ? 'repeat(2, minmax(40px, 1fr))' : undefined,
          gap: horizontal ? 6 : undefined,
          padding: horizontal ? '7px 10px' : undefined,
          flexDirection: horizontal ? undefined : 'column',
          overflow: 'auto',
        }}>
          {items.map((item, index) => (
            <React.Fragment key={item.label}>
            {!horizontal && item.section && items[index - 1]?.section !== item.section && (
              <div className="sweetspot-depth-section-title">{item.section}</div>
            )}
            <div style={{
              flex: horizontal ? undefined : '0 0 auto',
              display: 'flex',
              alignItems: horizontal ? 'flex-start' : 'center',
              justifyContent: 'space-between',
              flexDirection: horizontal ? 'column' : 'row',
              gap: 3,
              padding: horizontal ? '7px 10px' : '7px 12px',
              border: horizontal ? '1px solid #ECEEF1' : 'none',
              borderBottom: horizontal ? undefined : '1px solid #ECEEF1',
              borderRadius: horizontal ? 4 : 0,
              background: horizontal ? '#FAFBFC' : undefined,
              overflow: 'hidden',
            }}>
              <span style={{
                minWidth: 0, flex: '1 1 auto', overflow: 'hidden', color: '#71849A', fontSize: 11,
                lineHeight: '16px', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{item.label}</span>
              <span style={{
                flex: '0 0 auto', color: item.color || '#1E2A3A',
                fontSize: 14, fontWeight: 700, lineHeight: '20px', textAlign: 'right', whiteSpace: 'nowrap',
              }}>
                {formatValue(item)}{formatValue(item) === '--' || !item.unit ? '' : ` ${item.unit}`}
              </span>
            </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </section>
  );
}
